import {
  Button,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { AddCircle, MoreHoriz } from '@material-ui/icons';
import CreateIcon from '@material-ui/icons/Create';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import SearchIcon from '@material-ui/icons/Search';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BRAND_CREATE, BRAND_EDIT, BRAND_VIEW } from '../../../../constants/PropertyConst';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_BRANDS,
} from '../../../../constants/RoleConsts';
import DeletePopupModel from '../../../../shared/delete-popup/Delete-Popup';
import TablePaginationActions from '../../../../shared/table-pagination/TablePaginationActions';
import { FormattingUtils } from '../../../../utils/DataFormattingUtils';
import { notificationManager } from '../../../../utils/NotificationUtils';
import { deleteBrandById } from '../../../../utils/ServicesAPIUtils';
import { allGroupsSelector, fetchAllProperties, filteredBrandsSelector } from '../../propertySlice';
import VisibilityIcon from '@mui/icons-material/Visibility';

const BrandList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
    isPropertyAdmin = false,
  } = props;
  const dispatch = useDispatch();
  const groups = useSelector(allGroupsSelector);
  const originalBrands = useSelector(filteredBrandsSelector);
  const [brands, setBrands] = useState([]);
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
  });
  const [tempSearchText, setTempSearchText] = useState('');
  const [searchButton, setSearchButton] = useState(true);

  useEffect(() => {
    if (originalBrands) {
      setBrands(originalBrands);
      setTempSearchText('');
      setSearchButton(true);
    }
  }, [originalBrands]);

  useEffect(() => {
    if (brands?.length === 0) return;
    const totalItems = brands?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [brands?.length, rowsPerPage, page, onsetPage]);

  const setSearchText = (searchText) => {
    setTempSearchText(searchText);
    const filteredRows = originalBrands.filter((item) => {
      return (
        item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.code.toLowerCase().includes(searchText.toLowerCase())
      );
    });
    onsetPage(0);
    setBrands(filteredRows);
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onOpenDrawer = (type) => {
    onDrawerToggle(type, true, selectedValue.brand);
    handleClose();
  };

  const deleteBrand = async () => {
    handleClose();
    try {
      await deleteBrandById(selectedValue.id, selectedValue.groupId);

      dispatch(fetchAllProperties());
      notificationManager('success', 'Brand deleted successfully');
      setSelectedValue({
        id: '',
        name: '',
        title: '',
        description: '',
        status: false,
        brand: null,
      });
    } catch (e) {
      notificationManager(
        'danger',
        e.graphQLErrors ? e.graphQLErrors[0].message : 'Brand deletion failed',
      );
    }
  };

  const handleCellMenuClick = (event, brand) => {
    setSelectedValue({
      id: brand.id,
      name: brand.name,
      groupId: brand.groupId,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      brand: brand,
    });
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      brand: null,
    });
  };

  const handleDeletePopupOpen = () => {
    handleClose();
    let currVal = { ...selectedValue };
    currVal['title'] = 'Are you sure you want to delete';
    currVal['description'] =
      'This action cannot be reversed. Properties associated with this brand will be lost.';
    currVal.status = true;
    currVal.anchorEl = null;
    setSelectedValue(currVal);
  };

  const onDeleteCancel = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
    });
  };

  const onViewDetails = (brand, e) => {
    if (e.target.className === 'MuiTableCell-root MuiTableCell-body') {
      onViewToggle(BRAND_VIEW, true, brand);
    }
  };

  const onClickView = (e) => {
    if (selectedValue && selectedValue.brand) {
      onViewToggle(BRAND_VIEW, true, selectedValue.brand);
    }
  };

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {selectedValue.id && selectedValue.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteBrand}
          onDeleteCancel={onDeleteCancel}
        />
      )}
      <div className='row mx-2 d-flex'>
        <div className='col col-lg-4'>
          <TextField
            className='w-100'
            onChange={(e) => {
              setSearchText(e.target.value);
              setSearchButton(e?.target?.value ? false : true);
            }}
            value={tempSearchText}
            label='Search'
            InputProps={
              searchButton && {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }
          />
        </div>
        <div className='col-12 col-lg-8 d-flex justify-content-end'>
          {permissions[RBAC_TYPE_BRANDS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS &&
            !isPropertyAdmin && (
              <Button
                variant='contained'
                color='primary'
                size='large'
                startIcon={<AddCircle />}
                onClick={(e) => {
                  onOpenDrawer(BRAND_CREATE);
                }}
              >
                Add Brand
              </Button>
            )}
        </div>
      </div>
      <div className='row mx-2 d-flex mt-4'>
        <TableContainer>
          <Table className='list-table' aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell className='w-30'>Brand Name</TableCell>
                <TableCell className='w-20'>Brand Code</TableCell>
                <TableCell className='w-40'>Hotel Group</TableCell>
                <TableCell className='w-10' align='right'></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? brands.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : brands
              ).map((brand, i) => (
                <TableRow
                  key={i}
                  className='border-white pointer'
                  onClick={(e) => {
                    onViewDetails(brand, e);
                  }}
                >
                  <TableCell>{brand.name}</TableCell>
                  <TableCell>{brand.code}</TableCell>
                  <TableCell>{FormattingUtils.getGroupName(groups, brand.groupId)}</TableCell>
                  <TableCell align='right'>
                    {permissions[RBAC_TYPE_BRANDS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                      <Button
                        aria-controls='simple-menu'
                        aria-haspopup='true'
                        onClick={(e) => {
                          handleCellMenuClick(e, brand);
                        }}
                      >
                        <MoreHoriz />
                      </Button>
                    )}
                    <Menu
                      id={i}
                      anchorEl={selectedValue?.anchorEl}
                      disableRestoreFocus
                      MenuListProps={{ autoFocusItem: false }}
                      keepMounted
                      open={Boolean(selectedValue.anchorEl) && selectedValue.id === brand.id}
                      onClose={handleClose}
                      className='shadow'
                    >
                      <MenuItem
                        className='pe-4'
                        onClick={(e) => {
                          onOpenDrawer(BRAND_EDIT);
                        }}
                      >
                        <CreateIcon className='me-2 action-menu-icon' />
                        Edit
                      </MenuItem>
                      <MenuItem
                        className='pe-4'
                        onClick={(e) => {
                          onClickView(e);
                        }}
                      >
                        <VisibilityIcon className='me-2 action-menu-icon' />
                        View
                      </MenuItem>
                      {!isPropertyAdmin && (
                        <MenuItem
                          className='pe-4'
                          onClick={(e) => {
                            handleDeletePopupOpen();
                          }}
                        >
                          <DeleteForeverIcon className='me-2 action-menu-icon' />
                          Delete
                        </MenuItem>
                      )}
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className='paginator'>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  count={brands.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default BrandList;
